"use client"

/**
 * ScrollRegion — keyboard-accessible overflow container.
 *
 * Satisfies axe `scrollable-region-focusable` (Safari keyboard scroll parity)
 * **only while content overflows**. Non-overflowing regions stay out of the
 * Tab order so short breadcrumbs / fitting tables do not take a full-width
 * focus stop before their real controls.
 *
 * Use for any clipped `overflow-*` region that is not already a DS primitive
 * (`HorizontalScrollRegion`, `ScrollArea`, `Table`, `CardScrollRegion`).
 */

import * as React from "react"

import { composeRefs } from "../../lib/compose-refs"
import { cn } from "../../lib/utils"

/** Watch the viewport and its direct children — a child growing changes overflow too. */
function observeTree(ro: ResizeObserver, el: HTMLElement) {
  ro.disconnect()
  ro.observe(el)
  for (const child of el.children) {
    ro.observe(child)
  }
}

export type ScrollRegionProps = React.ComponentProps<"div"> & {
  /** Accessible name for the scroll viewport. */
  label: string
}

export const ScrollRegion = React.forwardRef<HTMLDivElement, ScrollRegionProps>(
  function ScrollRegion({ label, className, children, tabIndex, ...props }, ref) {
    const localRef = React.useRef<HTMLDivElement | null>(null)
    const [overflowing, setOverflowing] = React.useState(false)

    React.useLayoutEffect(() => {
      const el = localRef.current
      if (!el) return

      const sync = () => {
        setOverflowing(
          el.scrollWidth > el.clientWidth + 1 ||
            el.scrollHeight > el.clientHeight + 1,
        )
      }

      const ro = new ResizeObserver(sync)
      observeTree(ro, el)
      sync()
      el.addEventListener("scroll", sync, { passive: true })
      const mo = new MutationObserver(() => {
        observeTree(ro, el)
        sync()
      })
      mo.observe(el, { childList: true, subtree: true })

      return () => {
        ro.disconnect()
        mo.disconnect()
        el.removeEventListener("scroll", sync)
      }
    }, [])

    const resolvedTabIndex =
      tabIndex !== undefined ? tabIndex : overflowing ? 0 : undefined

    return (
      <div
        ref={composeRefs(localRef, ref)}
        data-slot="scroll-region"
        role="region"
        tabIndex={resolvedTabIndex}
        aria-label={label}
        className={cn(
          "outline-none",
          overflowing &&
            "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    )
  },
)

ScrollRegion.displayName = "ScrollRegion"
